import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { BarChart2, Target, Database, Shield, FileText, LogOut, Users, ShieldAlert, User, ChevronLeft, ChevronRight, Ticket, AlertTriangle, ShieldCheck, Key, Lock, Activity, Mail, Globe, Search, Code2, Eye } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const SIDEBAR_STORAGE_KEY = 'maphra:sidebar-collapsed';
const MOBILE_NAV_EVENT = 'maphra:toggle-navigation';

const Navigation = () => {
  const { pathname } = useLocation();
  const { logout, isAdmin, user } = useAuth();
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(() => {
    try {
      return window.localStorage.getItem(SIDEBAR_STORAGE_KEY) === 'true';
    } catch {
      return false;
    }
  });

  useEffect(() => {
    const toggleMobileNavigation = () => setIsMobileOpen(current => !current);
    window.addEventListener(MOBILE_NAV_EVENT, toggleMobileNavigation);
    return () => window.removeEventListener(MOBILE_NAV_EVENT, toggleMobileNavigation);
  }, []);

  useEffect(() => {
    setIsMobileOpen(false);
  }, [pathname]);

  const isActive = (path) => pathname === path;
  const toggleSidebar = () => {
    setIsCollapsed((currentValue) => {
      const nextValue = !currentValue;
      try {
        window.localStorage.setItem(SIDEBAR_STORAGE_KEY, String(nextValue));
      } catch {
        // The sidebar should still work when storage is unavailable.
      }
      return nextValue;
    });
  };
  const compact = isCollapsed && !isMobileOpen;

  const userNavItems = [
    // Grouped by what the user is trying to do, not by which service happens to
    // back it. Pages that overlap in concern sit adjacent under one heading:
    // Attack Surface + its Issues, Email Monitoring + Breach Monitor, Brand +
    // SocialPulse. Feature gating is unchanged - every `feature` key is
    // preserved so entitlement filtering still applies.
    { path: '/dashboard', icon: <BarChart2 className="h-5 w-5" />, text: 'Dashboard', section: 'Overview' },
    { path: '/reports', icon: <FileText className="h-5 w-5" />, text: 'Reports', section: 'Overview' },

    { path: '/external-attack-surface', icon: <Globe className="h-5 w-5" />, text: 'Attack Surface', feature: 'attackSurface', section: 'Attack Surface' },
    { path: '/attack-surface-issues', icon: <ShieldAlert className="h-5 w-5" />, text: 'Issues', feature: 'attackSurface', section: 'Attack Surface' },
    { path: '/main-domains', icon: <Globe className="h-5 w-5" />, text: 'Main Domains', feature: 'subdomainEnumeration', section: 'Attack Surface' },
    { path: '/subdomains', icon: <Globe className="h-5 w-5" />, text: 'Subdomains', feature: 'subdomainEnumeration', section: 'Attack Surface' },

    { path: '/security', icon: <ShieldAlert className="h-5 w-5" />, text: 'Security Analysis', section: 'Security Posture' },
    { path: '/ssl-security', icon: <Key className="h-5 w-5" />, text: 'SSL / TLS', feature: 'sslSecurity', section: 'Security Posture' },
    { path: '/http-security', icon: <Lock className="h-5 w-5" />, text: 'HTTP Headers', feature: 'httpSecurity', section: 'Security Posture' },
    { path: '/cms-security', icon: <Code2 className="h-5 w-5" />, text: 'CMS Security', feature: 'attackSurface', section: 'Security Posture' },

    { path: '/google-exposure', icon: <Search className="h-5 w-5" />, text: 'Search Exposure', feature: 'attackSurface', section: 'Exposure' },
    { path: '/hacked-email-monitoring', icon: <Mail className="h-5 w-5" />, text: 'Email Monitoring', feature: 'emailMonitoring', section: 'Exposure' },
    { path: '/breach-monitor', icon: <AlertTriangle className="h-5 w-5" />, text: 'Breach Monitor', feature: 'breachMonitor', section: 'Exposure' },

    { path: '/brand-monitoring', icon: <Shield className="h-5 w-5" />, text: 'Brand Monitoring', feature: 'brandMonitoring', section: 'Brand & Social' },
    { path: '/social-pulse', icon: <Activity className="h-5 w-5" />, text: 'SocialPulse', feature: 'socialPulse', section: 'Brand & Social' },

    { path: '/intelligence-hub', icon: <Eye className="h-5 w-5" />, text: 'Intelligence Hub', feature: 'attackSurface', section: 'Intelligence' },
    { path: '/intelligence-coverage', icon: <Activity className="h-5 w-5" />, text: 'Source Coverage', feature: 'attackSurface', section: 'Intelligence' },
    { path: '/threat-advisory', icon: <ShieldCheck className="h-5 w-5" />, text: 'Threat Advisory', feature: 'threatAdvisory', section: 'Intelligence' },
    { path: '/data', icon: <Database className="h-5 w-5" />, text: 'Data Explorer', section: 'Intelligence' },

    { path: '/scans', icon: <Target className="h-5 w-5" />, text: 'Scans', section: 'Operations' },
    { path: '/notifications', icon: <Mail className="h-5 w-5" />, text: 'Notifications', section: 'Operations' },
    { path: '/tickets', icon: <Ticket className="h-5 w-5" />, text: 'Support Tickets', section: 'Operations' },
  ];

  const adminNavItems = [
    { path: '/admin', icon: <Shield className="h-5 w-5" />, text: 'Admin Dashboard', section: 'Administration' },
    { path: '/admin/smtp', icon: <Mail className="h-5 w-5" />, text: 'SMTP Management', section: 'Administration' },
    { path: '/admin/users', icon: <Users className="h-5 w-5" />, text: 'Users & Access', section: 'Administration' },
    { path: '/admin/scans', icon: <Target className="h-5 w-5" />, text: 'All Scans', section: 'Administration' },
    { path: '/admin/notifications', icon: <Mail className="h-5 w-5" />, text: 'Notification Policy', section: 'Administration' },
    { path: '/admin/tickets', icon: <Ticket className="h-5 w-5" />, text: 'Manage Tickets', section: 'Administration' },
    { path: '/admin/threat-advisory', icon: <ShieldCheck className="h-5 w-5" />, text: 'Threat Management', section: 'Administration' },
    { path: '/admin/cvemap', icon: <Database className="h-5 w-5" />, text: 'CVE Intelligence', section: 'Administration' },
    { path: '/admin/integrations', icon: <Key className="h-5 w-5" />, text: 'Integrations', section: 'Administration' },
  ];

  const visibleUserNavItems = isAdmin
    ? userNavItems
    : userNavItems.filter(item => !item.feature || user?.featureAccess?.[item.feature] === true);
  const navItems = isAdmin ? [...adminNavItems, ...visibleUserNavItems] : visibleUserNavItems;

  return (
    <>
      {isMobileOpen && (
        <button
          type="button"
          className="fixed inset-0 z-40 bg-slate-950/60 backdrop-blur-sm md:hidden"
          onClick={() => setIsMobileOpen(false)}
          aria-label="Close navigation"
          data-testid="mobile-navigation-backdrop"
        />
      )}
      <aside
        className={`${compact ? 'md:w-20' : 'md:w-64'} fixed inset-y-0 left-0 z-50 flex h-full w-64 flex-shrink-0 flex-col bg-gray-800 text-white shadow-2xl transition-all duration-300 ease-in-out motion-reduce:transition-none md:relative md:inset-auto md:z-auto md:translate-x-0 md:shadow-none ${
          isMobileOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
        data-collapsed={compact}
        data-mobile-open={isMobileOpen}
      >
      <button
        type="button"
        onClick={() => setIsMobileOpen(false)}
        className="absolute right-2 top-2 z-20 flex h-8 w-8 items-center justify-center rounded-full text-gray-300 hover:bg-gray-700 hover:text-white md:hidden"
        aria-label="Close navigation"
      >
        <ChevronLeft className="h-5 w-5" aria-hidden="true" />
      </button>
      <button
        type="button"
        onClick={toggleSidebar}
        className="absolute -right-3 top-5 z-20 hidden h-7 w-7 items-center justify-center rounded-full border border-gray-600 bg-gray-800 text-gray-300 shadow-md transition-colors hover:bg-blue-600 hover:text-white focus:outline-none focus:ring-2 focus:ring-blue-400 focus:ring-offset-2 focus:ring-offset-gray-800 md:flex"
        aria-label={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        aria-controls="maphra-primary-navigation"
        aria-expanded={!isCollapsed}
        data-testid="sidebar-toggle"
        title={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
      >
        {isCollapsed
          ? <ChevronRight className="h-4 w-4" aria-hidden="true" />
          : <ChevronLeft className="h-4 w-4" aria-hidden="true" />}
      </button>

      <div className={`p-4 mb-6 flex items-center ${compact ? 'justify-center' : ''}`}>
        <Shield className="h-8 w-8 flex-shrink-0 text-blue-400" aria-hidden="true" />
        {!compact && (
          <>
            <h1 className="text-xl font-bold ml-3 whitespace-nowrap">Maphra</h1>
            <span className="ml-2 text-sm opacity-80 whitespace-nowrap">by Dedsec<sup className="ml-0.5 text-[10px]">™</sup></span>
          </>
        )}
      </div>

      <div className={`${compact ? 'px-2' : 'px-4'} mb-6`}>
        <Link
          to="/profile"
          className={`block bg-gray-700/50 rounded-lg border border-gray-600/50 transition-colors hover:bg-gray-700 ${compact ? 'p-2' : 'p-3'}`}
          aria-label={compact ? `View profile for ${user?.username || 'User'}` : undefined}
          title={compact ? `Profile: ${user?.username || 'User'}` : undefined}
        >
          <div className={`flex items-center ${compact ? 'justify-center' : ''}`}>
            <div className="bg-gradient-to-r from-blue-500 to-indigo-600 rounded-full p-0.5">
              <div className="bg-gray-800 rounded-full p-1">
                <User className="h-5 w-5 text-blue-300" />
              </div>
            </div>
            {!compact && (
              <div className="ml-3 min-w-0">
                <div className="font-medium truncate">{user?.username || 'User'}</div>
                <span className="text-xs text-blue-300 flex items-center">
                  View Profile
                  <ChevronRight className="h-3 w-3 ml-1" />
                </span>
              </div>
            )}
          </div>
        </Link>
      </div>

      <nav
        id="maphra-primary-navigation"
        className={`flex-1 overflow-y-auto pb-4 custom-scrollbar ${compact ? 'px-2' : 'px-4'}`}
        aria-label="Primary navigation"
      >
        <ul className="space-y-2 min-h-full">
          {navItems.map((item, index) => (
            <React.Fragment key={item.path}>
              {!compact && (
                index === 0 || navItems[index - 1]?.section !== item.section
              ) && (
                <li className={`${index === 0 ? 'pt-0' : 'pt-4'} px-3 text-[10px] font-semibold uppercase tracking-[0.18em] text-gray-500`}>
                  {item.section}
                </li>
              )}
              <li>
              <Link
                to={item.path}
                className={`relative flex items-center w-full p-3 rounded-md transition-all duration-200 ${compact ? 'justify-center' : 'space-x-3'} ${
                  isActive(item.path)
                    ? 'bg-gradient-to-r from-blue-700 to-blue-600 shadow-lg'
                    : 'hover:bg-gray-700'
                }`}
                aria-label={compact ? item.text : undefined}
                title={compact ? item.text : undefined}
                style={{
                  animationDelay: `${index * 50}ms`,
                  animation: 'fadeIn 0.5s ease-out forwards'
                }}
              >
                <div className={`transition-transform duration-200 ${isActive(item.path) ? 'transform scale-110' : ''}`}>
                  {item.icon}
                </div>
                {!compact && (
                  <span className={`${isActive(item.path) ? 'font-medium' : ''}`}>{item.text}</span>
                )}
                {isActive(item.path) && (
                  <div className={`${compact ? 'absolute right-1.5' : 'ml-auto'} h-2 w-2 rounded-full bg-blue-300`}></div>
                )}
              </Link>
              </li>
            </React.Fragment>
          ))}
        </ul>
      </nav>

      <div className={`${compact ? 'p-2' : 'p-4'} mt-auto border-t border-gray-700`}>
        <button
          onClick={logout}
          className={`flex items-center w-full p-3 rounded-md text-gray-400 hover:bg-gray-700 hover:text-white transition-all duration-200 group ${compact ? 'justify-center' : 'space-x-3'}`}
          aria-label={compact ? 'Logout' : undefined}
          title={compact ? 'Logout' : undefined}
        >
          <LogOut className="h-5 w-5 group-hover:rotate-12 transition-transform duration-200" />
          {!compact && <span>Logout</span>}
        </button>
      </div>
      </aside>
    </>
  );
};

export default Navigation;
